// src/hooks/useAccountDetail.ts
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export function useAccountDetail(
  accountId?: string,
  startDate: string,
  endDate: string
) {
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [accountsMap, setAccountsMap] = useState({});
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balanceTotal, setBalanceTotal] = useState(0);

  useEffect(() => {
    if (!accountId) return;
    fetchData();
    fetchBalanceTotal();
  }, [accountId]);

  async function fetchData() {
    setLoading(true);

    const { data: acc } = await supabase
      .from("accounts")
      .select("*")
      .eq("id", accountId)
      .single();
    setAccount(acc);

    const { data: txs } = await supabase
      .from("transactions")
      .select(
        `*, transfers (
        id,
        amount,
        date,
        description,
        from_account_id,
        to_account_id, 
        
        from_account:accounts!transfers_from_account_id_fkey (id, name),
        to_account:accounts!transfers_to_account_id_fkey (id, name)
        
      )
      ,categories(
        *
      )`
      )
      .eq("account_id", accountId)
      .gte("date", startDate)
      .lte("date", endDate)
      .order("date", { ascending: false });
    setTransactions(txs || []);

    const { data: transferList } = await supabase.from("transfers").select("*");
    setTransfers(transferList || []);

    const { data: accs } = await supabase.from("accounts").select("id, name");
    const map = Object.fromEntries((accs || []).map((a) => [a.id, a.name]));
    setAccountsMap(map);

    setLoading(false);
  }

  const ingresos = transactions
    .filter((t: any) => t.type === "income")
    .reduce((sum, t: any) => sum + t.amount, 0);

  const egresos = transactions
    .filter((t: any) => t.type === "expense")
    .reduce((sum, t: any) => sum + t.amount, 0);

  const total = ingresos - egresos;

  const dailyData = transactions.reduce((acc: any, tx: any) => {
    const day = tx.date;
    if (!acc[day]) acc[day] = { date: day, income: 0, expense: 0 };
    if (tx.type === "income") acc[day].income += tx.amount;
    if (tx.type === "expense") acc[day].expense += tx.amount;
    return acc;
  }, {});

  const chartData = Object.values(dailyData).sort((a: any, b: any) =>
    a.date.localeCompare(b.date)
  );

  const fetchBalanceTotal = async () => {
    // Obtener el balance total sin rango de fechas
    const { data: allTxs } = await supabase
      .from("transactions")
      .select("amount, type")
      .eq("account_id", accountId);

    const ingresosTotales =
      allTxs
        ?.filter((t: any) => t.type === "income")
        .reduce((sum, t: any) => sum + t.amount, 0) || 0;

    const egresosTotales =
      allTxs
        ?.filter((t: any) => t.type === "expense")
        .reduce((sum, t: any) => sum + t.amount, 0) || 0;

    setBalanceTotal(
      (account?.initial_balance || 0) + ingresosTotales - egresosTotales
    );
  };

  return {
    account,
    transactions,
    transfers,
    accountsMap,
    ingresos,
    egresos,
    total,
    chartData,
    fetchData,
    loading,
    balanceTotal,
  };
}
